import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import toastr from "toastr";
import HolidayModal from "../../components/Modals/leaveinfos/HolidayModal";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const statusConfig = {
  Approved: { color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" },
  Declined: { color: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
  Pending: { color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
  New: { color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe" },
};

const leaveTypeColor = {
  "Medical Leave": "#3b82f6",
  "Casual Leave": "#10b981",
  "Discretionary Leave": "#8b5cf6",
  Maternity: "#ec4899",
  "Other Leave": "#f97316",
};

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};
const cardVariants = {
  hidden: { opacity: 0, y: 32, scale: 0.96 },
  visible: { opacity: 1, y: 0, scale: 1,
    transition: { type: "spring", stiffness: 200, damping: 22 },
  },
};

// ─── Donut Chart ──────────────────────────────────────────────────────────────
function DonutChart({ value, total, label, color }) {
  const r = 52;
  const circumference = 2 * Math.PI * r;
  const pct = total > 0 ? Math.min(Math.max(value / total, 0), 1) : 0;
  const dash = pct * circumference;
  const displayVal = Number.isFinite(value)
    ? value % 1 === 0
      ? value
      : value.toFixed(1)
    : 0;

  return (
    <div style={{ textAlign: "center" }}>
      <div
        style={{
          position: "relative",
          width: 136,
          height: 136,
          margin: "0 auto",
        }}
      >
        <svg width="136" height="136" viewBox="0 0 136 136">
          <circle
            cx="68"
            cy="68"
            r={r}
            fill="none"
            stroke="#e5e7eb"
            strokeWidth="10"
          />
          <circle
            cx="68"
            cy="68"
            r={r}
            fill="none"
            stroke={color}
            strokeWidth="10"
            strokeDasharray={`${dash} ${circumference}`}
            strokeLinecap="round"
            transform="rotate(-90 68 68)"
            style={{ transition: "stroke-dasharray 0.9s ease" }}
          />
        </svg>
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span
            style={{
              fontSize: 26,
              fontWeight: 800,
              color: "#111827",
              lineHeight: 1,
            }}
          >
            {displayVal}
          </span>
          <span
            style={{
              fontSize: 10,
              color: "#6b7280",
              marginTop: 3,
              fontWeight: 700,
              letterSpacing: "0.05em",
            }}
          >
            DAYS
          </span>
        </div>
      </div>
      <p
        style={{
          margin: "10px 0 0",
          fontSize: 12,
          fontWeight: 600,
          color: "#374151",
        }}
      >
        {label}
      </p>
    </div>
  );
}

// ─── Stat Card ────────────────────────────────────────────────────────────────
function StatCard({ card, index }) {
  const [hovered, setHovered] = useState(false);
  const displayVal = Number.isFinite(card.value)
    ? card.value % 1 === 0
      ? card.value
      : card.value.toFixed(1)
    : 0;

  return (
    <motion.div
      variants={cardVariants}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      whileHover={{ y: -6, scale: 1.02 }}
      style={{
        background: card.bg,
        borderRadius: 18,
        padding: "20px 20px 16px",
        position: "relative",
        overflow: "hidden",
        cursor: "default",
        border: "1px solid rgba(255,255,255,0.08)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        minHeight: 148,
        boxShadow: hovered
          ? `0 12px 40px rgba(0,0,0,0.4), 0 0 0 1px ${card.accent}44`
          : "0 4px 20px rgba(0,0,0,0.25)",
        transition: "box-shadow 0.3s",
      }}
    >
      {/* background emoji */}
      <motion.div
        animate={{ scale: hovered ? 1.6 : 1, opacity: hovered ? 0.15 : 0.08 }}
        transition={{ duration: 0.4 }}
        style={{
          position: "absolute",
          top: -20,
          right: -20,
          fontSize: 80,
          pointerEvents: "none",
          filter: "blur(2px)",
        }}
      >
        {card.icon}
      </motion.div>

      {/* top section — label + big number */}
      <div style={{ position: "relative", zIndex: 1 }}>
        <p
          style={{
            color: "rgba(255,255,255,0.6)",
            margin: "0 0 6px",
            fontSize: 12,
            fontWeight: 500,
            lineHeight: 1.3,
          }}
        >
          {card.label}
        </p>
        <motion.h3
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{
            delay: index * 0.08 + 0.3,
            type: "spring",
            stiffness: 300,
          }}
          style={{
            color: "#fff",
            margin: 0,
            fontSize: 34,
            fontWeight: 800,
            lineHeight: 1,
          }}
        >
          {displayVal}
        </motion.h3>
        <p
          style={{
            color: "rgba(255,255,255,0.35)",
            margin: "4px 0 0",
            fontSize: 11,
          }}
        >
          days
        </p>
      </div>

      {/* bottom section — sub-label pill, always visible, never changes with deductions */}
      {card.subLabel && (
        <div
          style={{
            position: "relative",
            zIndex: 1,
            marginTop: 14,
            padding: "6px 10px",
            borderRadius: 8,
            background: "rgba(255,255,255,0.07)",
            border: "1px solid rgba(255,255,255,0.10)",
            fontSize: 11,
            color: "rgba(255,255,255,0.50)",
            fontWeight: 500,
            letterSpacing: "0.01em",
            display: "flex",
            alignItems: "center",
            gap: 5,
            overflow: "hidden",
          }}
        >
          <span
            style={{
              display: "inline-block",
              width: 5,
              height: 5,
              borderRadius: "50%",
              background: card.accent,
              flexShrink: 0,
              opacity: 0.8,
            }}
          />
          <span
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {card.subLabel}
          </span>
        </div>
      )}
    </motion.div>
  );
}

// ─── Tooltip ──────────────────────────────────────────────────────────────────
function TooltipInfo({ text }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: "relative", display: "inline-flex" }}>
      <span
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        style={{
          cursor: "pointer",
          color: "#60a5fa",
          fontSize: 15,
          marginLeft: 4,
        }}
      >
        ⓘ
      </span>
      {show && (
        <div
          style={{
            position: "absolute",
            bottom: "130%",
            left: "50%",
            transform: "translateX(-50%)",
            background: "#1e293b",
            border: "1px solid #334155",
            borderRadius: 10,
            padding: "8px 12px",
            fontSize: 12,
            color: "#e2e8f0",
            whiteSpace: "normal",
            maxWidth: 220,
            zIndex: 200,
            boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
          }}
        >
          {text}
        </div>
      )}
    </div>
  );
}

// ─── Status Badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status, onChange }) {
  const [open, setOpen] = useState(false);
  const cfg = statusConfig[status] || statusConfig.Pending;

  return (
    <div style={{ position: "relative" }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "4px 12px",
          borderRadius: 20,
          border: `1.5px solid ${cfg.border}`,
          background: cfg.bg,
          color: cfg.color,
          cursor: "pointer",
          fontSize: 12,
          fontWeight: 600,
          fontFamily: "inherit",
          whiteSpace: "nowrap",
        }}
      >
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: cfg.color,
            display: "inline-block",
            animation:
              status === "Pending" ? "badgePulse 1.6s infinite" : "none",
          }}
        />
        {status}
        <span style={{ fontSize: 9, marginLeft: 1 }}>▾</span>
      </button>

      {open && (
        <>
          <div
            onClick={() => setOpen(false)}
            style={{ position: "fixed", inset: 0, zIndex: 98 }}
          />
          <div
            style={{
              position: "absolute",
              top: "110%",
              left: 0,
              zIndex: 99,
              background: "#fff",
              border: "1px solid #e2e8f0",
              borderRadius: 12,
              boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
              minWidth: 140,
              overflow: "hidden",
            }}
          >
            {Object.entries(statusConfig).map(([key, c]) => (
              <button
                key={key}
                onClick={() => {
                  onChange(key);
                  setOpen(false);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  width: "100%",
                  padding: "9px 14px",
                  background: "transparent",
                  border: "none",
                  color: c.color,
                  cursor: "pointer",
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: "inherit",
                  textAlign: "left",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#f8fafc")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "transparent")
                }
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: c.color,
                    display: "inline-block",
                  }}
                />
                {key}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function TeamLeaveView({ viewUserId = null }) {
  const { token, setGlobalLoading } = useOutletContext();
  const currentYear = new Date().getFullYear();
  const [leaves, setLeaves] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [stats, setStats] = useState({});
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");
  const [selectedRows, setSelectedRows] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newLeave, setNewLeave] = useState({type: "Casual", from: "", to: "", days: 1, approvedBy: "", role: "", reason: "",});
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [showHolidayModal, setShowHolidayModal] = useState(false);

  // ── Fetch leaves ──────────────────────────────────────────────────────────
  const fetchLeaves = async (year = selectedYear) => {
    setGlobalLoading(true);
    try {
        const params = { year };
        if (viewUserId !== null && viewUserId !== undefined && viewUserId !== "") {
          params.userId = Number(viewUserId);
        }
      const res = await axios.get(`${API_BASE_URL}/employeeinfos/leaves`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      
      if (res.status === 200) {
        console.log("FULL STATS FROM BACKEND 👉", res.data.leaveTypes);
        setLeaves(res.data.data || []);
        setHolidays(res.data.holidays || []);
        setStats(res.data.stats || {});
        // setLeaveTypes([{ id: 0, name: "All" }, ...(res.data.leaveTypes || [])]);
        setLeaveTypes(res.data.leaveTypes || []);
      }
    } catch (error) {
      toastr.error(error?.response?.data?.message || "Failed to fetch leaves");
    } finally {
      setGlobalLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaves(selectedYear);
  }, [selectedYear, viewUserId]);

  // ── Derived values ───────────────────────────────────────────────────────
  // totalYearlyLeave  = full-year entitlement (e.g. 24 or 20) — fixed constant for the year
  // accruedLeave      = carryForward + yearlyLeave (gross, never shrinks with deductions)
  // appliedLeave      = leave taken (excludes Discretionary + Maternity)
  // remainingAtDecember = balance if no more leaves taken this year
  const totalEntitlement = parseFloat(((stats.carryForward || 0) + (stats.totalYearlyLeave || 0)).toFixed(1));
  const grossAccrued = parseFloat((stats.remainingLeave || 0).toFixed(1));
  const taken = parseFloat((stats.appliedLeave || 0).toFixed(1));
  const remainingAtDecFmt = parseFloat((stats.remainingAtDecember || 0).toFixed(1));
  const carryFwd = Number(stats.carryForward) || 0;

  // Count how many non-Declined Discretionary leaves exist this year (max 1 allowed)
  const discretionaryUsed = leaves.filter((l) => l.type === "Discretionary Leave" && l.status !== "Declined").length;

  // ── Stat cards ────────────────────────────────────────────────────────────
  // subLabel is always the FIXED yearly context — it never changes with deductions.
  // This lets users always see what their total entitlement is and how accrual grows.
  const yearlyEntitlement = parseFloat((stats.totalYearlyLeave || 0).toFixed(1));
  const statCards = [
    {
      label: `Balance (Till Dec ${selectedYear})`,
      value: remainingAtDecFmt,
      // sub: shows the full entitlement (CF + full year) so user knows the ceiling
      subLabel: `of ${totalEntitlement} total (${carryFwd} CF + ${yearlyEntitlement} yearly accrued leaves)`,
      icon: "📅",
      accent: "#7c3aed",
      bg: "linear-gradient(135deg, #2d1b69 0%, #1a1a2e 100%)",
    },
    {
      label: "Carry Forward (CF)",
      value: carryFwd,
      // sub: constant — tells user where these days came from
      subLabel: `carried over from ${selectedYear - 1} (lapsed on June 30th)`,
      icon: "🔁",
      accent: "#0ea5e9",
      bg: "linear-gradient(135deg, #0f3460 0%, #16213e 100%)",
    },
    {
      label: "Leaves Taken (LT)",
      value: taken,
      // sub: shows gross accrued as the denominator so user can see utilisation
     
      icon: "📉",
      accent: "#ef4444",
      bg: "linear-gradient(135deg, #3a0e0e 0%, #1a1a2e 100%)",
    },
    {
      // Gross accrued = CF + monthly accrual till today — NEVER decreases on leave taken.
      // This is what keeps growing every month and never goes down.
      label: "Total Accrued Leaves till Today (A)",
      value:  parseFloat((stats.yearlyLeave || 0).toFixed(1)),
      subLabel: `${taken} taken | ${parseFloat((grossAccrued ).toFixed(1))} days left (A + CF - LT)`,
      icon: "📈",
      accent: "#f59e0b",
      bg: "linear-gradient(135deg, #451a03 0%, #1a1a2e 100%)",
    },
  ];

  // ── CRUD handlers ─────────────────────────────────────────────────────────
  const updateStatus = async (leave, status) => {
    setGlobalLoading(true);
    try {
        const payload = {
            employeeinfo_has_leave_id: leave.id,
            employeeinfo_id: leave.employeeinfo_id,
            user_name: leave.user_name,
            from: leave.from,
            to: leave.to,
            no_of_days: leave.no_of_days,

            // Your existing backend expects status.value
            status: {
                value: status,
            },

            comments: status === "Approved"
                ? "Approved"
                : status === "Rejected"
                ? "Rejected"
                : "",
            };

      const res = await axios.post(`${API_BASE_URL}/employeeinfos/approve_leave`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.status === 200) {
        toastr.success(res.data.message || "Status updated");
        await fetchLeaves();
      }
    } catch (error) {
      if (error?.response?.status === 403)
        toastr.error(error.response.data.message);
    } finally {
      setGlobalLoading(false);
    }
  };


//   const deleteLeave = async (id, status) => {
//     if (status === "Approved") {
//       toastr.warning("Approved leaves cannot be deleted");
//       return;
//     }
//     setGlobalLoading(true);
//     try {
//       const res = await axios.delete(
//         `${API_BASE_URL}/employeeinfos/leaves/delete/${id}`,
//         { headers: { Authorization: `Bearer ${token}` } },
//       );
//       if (res.status === 200) {
//         toastr.success(res.data.message || "Leave deleted");
//         fetchLeaves();
//       }
//     } catch (error) {
//       toastr.error(error?.response?.data?.message || "Delete failed");
//     } finally {
//       setGlobalLoading(false);
//     }
//   };


  const deleteSelected = async () => {
    // FIX: filter out approved leaves before bulk delete
    const approvedSelected = filtered.filter(
      (l) => selectedRows.includes(l.id) && l.status === "Approved",
    );
    if (approvedSelected.length > 0) {
      toastr.warning(
        `${approvedSelected.length} approved leave(s) skipped — cannot be deleted`,
      );
    }
    const deletableIds = selectedRows.filter((id) => {
      const leave = filtered.find((l) => l.id === id);
      return leave && leave.status !== "Approved";
    });
    if (deletableIds.length === 0) return;

    setGlobalLoading(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/leaves/bulk_delete`,
        { ids: deletableIds },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.status === 200) {
        toastr.success(res.data.message || "Leaves deleted");
        setSelectedRows([]);
        fetchLeaves();
      }
    } catch (error) {
      toastr.error(error?.response?.data?.message || "Bulk delete failed");
    } finally {
      setGlobalLoading(false);
    }
  };


  // ── Filtering ─────────────────────────────────────────────────────────────
  const filtered = leaves.filter((l) => {
    const matchSearch =
      l.type?.toLowerCase().includes(search.toLowerCase()) ||
      (l.approvedBy || "").toLowerCase().includes(search.toLowerCase());
    const matchType = filterType === "All" || l.type === filterType;
    const matchStatus = filterStatus === "All" || l.status === filterStatus;
    return matchSearch && matchType && matchStatus;
  });

  const toggleRow = (id) =>
    setSelectedRows((s) =>
      s.includes(id) ? s.filter((r) => r !== id) : [...s, id],
    );
  const toggleAll = () => setSelectedRows(selectedRows.length === filtered.length ? [] : filtered.map((l) => l.id));
  const statusTypes = ["All", "Approved", "Declined", "Pending"];
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

  return (
    <div
      className="lv-page"
      style={{
        minHeight: "100vh",
        background: "#f8fafc",
        color: "#111827",
        fontFamily: '"DM Sans", "Segoe UI", sans-serif',
        padding: "28px 32px",
      }}
    >
      <style>{`
        @keyframes badgePulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.4; transform: scale(1.5); }
        }
        .leave-row { transition: background 0.15s; }
        .leave-row:hover { background: #f8fafc !important; }
        .action-btn { opacity: 0.4; transition: opacity 0.15s, transform 0.15s; }
        .action-btn:hover { opacity: 1; transform: scale(1.15); }
        .action-btn-disabled { opacity: 0.15; cursor: not-allowed !important; }
        .filter-input:focus  { border-color: #a855f7 !important; }
        .filter-select:focus { outline: none; border-color: #a855f7 !important; }

        /* ── Responsive ─────────────────────────────────────────────────── */
        .lv-page        { padding: 28px 32px; }
        .lv-cards-grid  { grid-template-columns: repeat(4, 1fr); }
        .lv-header      { flex-direction: row; }
        .lv-table-cell  { padding: 13px 16px; }

        @media (max-width: 900px) {
          .lv-cards-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 600px) {
          .lv-page       { padding: 16px 14px !important; }
          .lv-cards-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 10px !important; }
          .lv-title      { font-size: 20px !important; }
          .lv-table-cell { padding: 10px 10px !important; font-size: 12px !important; }
          .lv-hide-mobile { display: none !important; }
          .lv-filters    { flex-direction: column !important; width: 100% !important; }
          .lv-filter-input { width: 100% !important; }
          .lv-card-header { flex-direction: column !important; align-items: flex-start !important; }
        }
      `}</style>

      {/* ── Page Header ───────────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 28,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <h2
          className="lv-title"
          style={{
            margin: 0,
            fontSize: 26,
            fontWeight: 800,
            letterSpacing: "-0.5px",
            color: "#111827",
          }}
        >
          Leave Management
        </h2>

        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            style={{
              padding: "9px 14px",
              borderRadius: 10,
              border: "1.5px solid #e2e8f0",
              background: "#fff",
              color: "#374151",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>

          <motion.button
            whileHover={{ scale: 1.04, boxShadow: "0 0 24px rgba(59,130,246,0.35)"}}
            whileTap={{ scale: 0.96 }}
            onClick={() => setShowHolidayModal(true)}
            style={{
              padding: "9px 20px",
              borderRadius: 10,
              border: "1px solid #3b82f6",
              background: "#fff",
              color: "#2563eb",
              cursor: "pointer",
              fontSize: 14,
              fontWeight: 700,
              fontFamily: "inherit",
            }}
          >
            📅 Show Holidays
          </motion.button>
        </div>
      </motion.div>

      {/* ── Maternity banner ─────────────────────────────────────────────── */}
      {stats.onMaternity && (
        <div
          style={{
            background: "#fdf2f8",
            border: "1px solid #f0abfc",
            borderRadius: 12,
            padding: "12px 20px",
            marginBottom: 20,
            fontSize: 13,
            color: "#86198f",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          🤱 <strong>Maternity leave active</strong> — accrual is frozen during
          this period. Leave balance reflects this.
        </div>
      )}

      {/* ── Stat Cards ────────────────────────────────────────────────────── */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="lv-cards-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 16,
          marginBottom: 20,
        }}
      >
        {statCards.map((card, i) => (
          <StatCard key={card.label} card={card} index={i} />
        ))}
      </motion.div>

      {/* ── Leave List ────────────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35, duration: 0.45 }}
        style={{
          background: "#ffffff",
          borderRadius: 16,
          border: "1px solid #e2e8f0",
          boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
        }}
      >
        {/* Card Header */}
        <div
          className="lv-card-header"
          style={{
            padding: "16px 24px",
            borderBottom: "1px solid #f1f5f9",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <span style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>
              Leave List
            </span>
            <span
              style={{
                padding: "3px 10px",
                borderRadius: 99,
                background: "#f5f3ff",
                color: "#7c3aed",
                fontSize: 12,
                fontWeight: 600,
                border: "1px solid #ddd6fe",
              }}
            >
              Total: {leaves.length}
            </span>
            <span
              style={{
                padding: "3px 10px",
                borderRadius: 99,
                background: "#f1f5f9",
                color: "#64748b",
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              Showing: {filtered.length}
            </span>
            {selectedRows.length > 0 && (
              <button
                onClick={deleteSelected}
                style={{
                  padding: "4px 12px",
                  borderRadius: 8,
                  border: "none",
                  background: "#fee2e2",
                  color: "#dc2626",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                🗑 Delete ({selectedRows.length})
              </button>
            )}
          </div>

          <div
            className="lv-filters"
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <div style={{ position: "relative" }}>
              <input
                className="filter-input lv-filter-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                style={{
                  padding: "7px 12px 7px 34px",
                  borderRadius: 8,
                  border: "1.5px solid #e2e8f0",
                  background: "#f8fafc",
                  color: "#111827",
                  fontSize: 13,
                  outline: "none",
                  fontFamily: "inherit",
                  width: 170,
                  transition: "border-color 0.2s",
                }}
              />
              <span
                style={{
                  position: "absolute",
                  left: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#94a3b8",
                  fontSize: 14,
                }}
              >
                🔍
              </span>
            </div>

            <select
              className="filter-select"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              style={{
                padding: "7px 10px",
                borderRadius: 8,
                border: "1.5px solid #e2e8f0",
                background: "#f8fafc",
                color: "#374151",
                fontSize: 13,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              {leaveTypes.map((t) => (
                <option key={t.id} value={t.name}>
                  {t.name}
                </option>
              ))}
            </select>

            <select
              className="filter-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{
                padding: "7px 10px",
                borderRadius: 8,
                border: "1.5px solid #e2e8f0",
                background: "#f8fafc",
                color: "#374151",
                fontSize: 13,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              {statusTypes.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Table */}
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr
                style={{
                  background: "#fafafa",
                  borderBottom: "1px solid #f1f5f9",
                }}
              >
                <th style={{ padding: "11px 16px", width: 40 }}>
                  <input
                    type="checkbox"
                    checked={
                      selectedRows.length === filtered.length &&
                      filtered.length > 0
                    }
                    onChange={toggleAll}
                    style={{
                      accentColor: "#7c3aed",
                      cursor: "pointer",
                      width: 15,
                      height: 15,
                    }}
                  />
                </th>
                {[
                  "Leave Type",
                  "From",
                  "To",
                  "Days",
                  "Approved By",
                  "Status",
                ].map((h) => (
                  <th
                    key={h}
                    className={h === "Approved By" ? "lv-hide-mobile" : ""}
                    style={{
                      padding: "11px 16px",
                      textAlign: "left",
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#94a3b8",
                      letterSpacing: "0.07em",
                      textTransform: "uppercase",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    style={{
                      padding: 52,
                      textAlign: "center",
                      color: "#94a3b8",
                      fontSize: 14,
                    }}
                  >
                    {leaves.length === 0
                      ? `⏳ No leaves found for ${selectedYear}`
                      : "🔍 No leaves match your filters"}
                  </td>
                </tr>
              ) : (
                filtered.map((leave) => {
                  const typeColor = leaveTypeColor[leave.type] || "#94a3b8";
                  const isSelected = selectedRows.includes(leave.id);
                  const isApproved = leave.status === "Approved";
                  const days = parseFloat(
                    leave.num_leave_days ?? leave.working_days ?? 0,
                  );

                  return (
                    <tr
                      key={leave.id}
                      className="leave-row"
                      style={{
                        borderBottom: "1px solid #f1f5f9",
                        background: isSelected ? "#f5f3ff" : "#fff",
                      }}
                    >
                      <td style={{ padding: "13px 16px" }}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleRow(leave.id)}
                          style={{
                            accentColor: "#7c3aed",
                            cursor: "pointer",
                            width: 15,
                            height: 15,
                          }}
                        />
                      </td>

                      <td style={{ padding: "13px 16px" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 7,
                          }}
                        >
                          <span
                            style={{
                              display: "inline-block",
                              width: 3,
                              height: 20,
                              borderRadius: 99,
                              background: typeColor,
                              flexShrink: 0,
                            }}
                          />
                          <span
                            style={{
                              fontSize: 14,
                              fontWeight: 600,
                              color: "#111827",
                            }}
                          >
                            {leave.type}
                          </span>
                          {leave.reason && <TooltipInfo text={leave.reason} />}
                        </div>
                      </td>

                      <td
                        style={{
                          padding: "13px 16px",
                          fontSize: 13,
                          color: "#64748b",
                        }}
                      >
                        {leave.from
                          ? new Date(leave.from).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })
                          : "—"}
                      </td>

                      <td
                        style={{
                          padding: "13px 16px",
                          fontSize: 13,
                          color: "#64748b",
                        }}
                      >
                        {leave.to
                          ? new Date(leave.to).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })
                          : "—"}
                      </td>

                      <td style={{ padding: "13px 16px" }}>
                        <span
                          style={{
                            padding: "3px 10px",
                            borderRadius: 99,
                            background: "#f1f5f9",
                            color: "#374151",
                            fontSize: 12,
                            fontWeight: 700,
                          }}
                        >
                          {days % 1 === 0 ? days : days.toFixed(1)}d
                        </span>
                      </td>

                      <td
                        className="lv-hide-mobile"
                        style={{ padding: "13px 16px" }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 9,
                          }}
                        >
                          <div
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: "50%",
                              flexShrink: 0,
                              background:
                                "linear-gradient(135deg, #7c3aed, #a855f7)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 13,
                              fontWeight: 800,
                              color: "#fff",
                            }}
                          >
                            {leave.approvedBy?.charAt(0) || "?"}
                          </div>
                          <span
                            style={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: "#111827",
                            }}
                          >
                            {leave.approvedBy || "Pending"}
                          </span>
                        </div>
                      </td>

                      <td style={{ padding: "13px 16px" }}>
                        <StatusBadge
                          status={leave.status}
                          onChange={(s) => updateStatus(leave, s)}
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "12px 24px",
            borderTop: "1px solid #f1f5f9",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span style={{ fontSize: 13, color: "#94a3b8" }}>
            Showing 1 – {filtered.length} of {filtered.length} entries
          </span>
          <div style={{ display: "flex", gap: 6 }}>
            {["‹", "1", "›"].map((p, i) => (
              <button
                key={i}
                style={{
                  padding: "5px 11px",
                  borderRadius: 6,
                  border: "1px solid #e2e8f0",
                  background: p === "1" ? "#7c3aed" : "#fff",
                  color: p === "1" ? "#fff" : "#64748b",
                  cursor: "pointer",
                  fontSize: 13,
                  fontFamily: "inherit",
                }}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      </motion.div>

      <HolidayModal
        show={showHolidayModal}
        setShow={setShowHolidayModal}
        // worklocations={worklocations}
        year={selectedYear}
        token={token}
      />
    </div>
  );
}
